import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    VStack,
    Heading,
    Text,
    Button,
    Spinner,
    Alert,
    AlertIcon,
    Center,
    Icon,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { supabase } from './utils/supabaseClient';
import { useRouteConfig } from './providers/RouteConfigProvider';
import { getAppOrigin } from './utils/url';

export const VerifyEmail = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { fullCertRegistrationPath } = useRouteConfig();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'expired'
    const [message, setMessage] = useState('');
    const [registrationId, setRegistrationId] = useState(null);
    const hasVerifiedRef = useRef(false); // Prevent re-verification on refresh

    useEffect(() => {
        // Don't run if already verified (check both state and ref to prevent re-verification on refresh)
        if (status === 'success' || hasVerifiedRef.current) return;

        const verifyEmail = async () => {
            if (!token) {
                setStatus('error');
                setMessage('Invalid verification link');
                return;
            }

            // Check localStorage for previous successful verification with this token
            const storedVerification = localStorage.getItem(`email_verified_${token}`);
            if (storedVerification) {
                try {
                    const { registrationId: storedId, timestamp } = JSON.parse(storedVerification);
                    // If verified within last 24 hours, consider it still valid
                    const verifiedTime = new Date(timestamp);
                    const now = new Date();
                    const hoursSinceVerification = (now - verifiedTime) / (1000 * 60 * 60);
                    
                    if (hoursSinceVerification < 24 && storedId) {
                        setStatus('success');
                        setMessage('Email verified successfully!');
                        setRegistrationId(storedId);
                        hasVerifiedRef.current = true;
                        return;
                    }
                } catch (e) {
                    // Invalid stored data, continue with verification
                }
            }

            try {
                // Call the Edge Function with token in body (using invoke method)
                let data, error;
                
                try {
                    const result = await supabase.functions.invoke('verify-email', {
                        body: { token },
                    });
                    data = result.data;
                    error = result.error;
                } catch (invokeError) {
                    // If invoke throws, check database directly to see if verification succeeded
                    console.warn('Invoke error, checking database directly:', invokeError);
                    
                    // Try to find registration by token first
                    const { data: registrationByToken, error: tokenError } = await supabase
                        .from('registration')
                        .select('id, email_verified, participant_email, participant_name')
                        .eq('email_verification_token', token)
                        .maybeSingle();
                    
                    if (!tokenError && registrationByToken) {
                        if (registrationByToken.email_verified) {
                            // Verification succeeded despite the error!
                            setStatus('success');
                            setMessage('Email verified successfully!');
                            setRegistrationId(registrationByToken.id);
                            hasVerifiedRef.current = true;
                            // Store verification in localStorage
                            localStorage.setItem(`email_verified_${token}`, JSON.stringify({
                                registrationId: registrationByToken.id,
                                timestamp: new Date().toISOString()
                            }));
                            return; // Exit early - verification succeeded
                        }
                    }
                    
                    // Token not found - check if email was recently verified (within last hour)
                    // by looking for registrations with verified=true and verified_at timestamp
                    if (!tokenError && !registrationByToken) {
                        // Try to find recently verified registration by checking email_verified_at
                        // We can't query by token since it's null, but we can check if there's a pattern
                        // Actually, without the token or email, we can't find the specific registration
                        // So we'll just show the error message
                    }
                    
                    // Token not found - might have been cleared after verification
                    // Check if there's a recently verified registration (within last 5 minutes)
                    // by looking for registrations with null token and verified=true
                    // We'll use a different approach: check if the error indicates token not found
                    if (invokeError.message && invokeError.message.includes('non-2xx')) {
                        // Try to find any verified registration (as fallback)
                        // This is a best-effort check
                        setStatus('error');
                        setMessage('Verification token not found. If you already verified, please continue to registration.');
                        return;
                    }
                    
                    // If we can't verify from database, show error
                    throw invokeError;
                }

                // Handle response - check data first, then error
                if (data && data.success) {
                    setStatus('success');
                    setMessage(data.message || 'Email verified successfully!');
                    setRegistrationId(data.registrationId);
                    hasVerifiedRef.current = true;
                    // Store verification in localStorage to persist across refreshes
                    if (data.registrationId) {
                        localStorage.setItem(`email_verified_${token}`, JSON.stringify({
                            registrationId: data.registrationId,
                            timestamp: new Date().toISOString()
                        }));
                    }
                } else if (data && data.expired) {
                    setStatus('expired');
                    setMessage(data.error || 'Verification link has expired');
                    setRegistrationId(data.registrationId);
                } else if (data && data.tokenNotFound) {
                    // Token not found - check localStorage for previous verification
                    const storedVerification = localStorage.getItem(`email_verified_${token}`);
                    if (storedVerification) {
                        try {
                            const { registrationId: storedId } = JSON.parse(storedVerification);
                            if (storedId) {
                                setStatus('success');
                                setMessage('Email already verified! You can continue to your registration.');
                                setRegistrationId(storedId);
                                hasVerifiedRef.current = true;
                                return;
                            }
                        } catch (e) {
                            // Invalid stored data, continue with error
                        }
                    }
                    // Token not found - might already be verified or expired
                    setStatus('error');
                    setMessage('This verification link is no longer valid. It may have already been used, expired, or you may have requested a new verification email. If you already verified your email, you can continue to your registration.');
                } else if (error) {
                    // If we get an error, check database to see if verification succeeded anyway
                    console.warn('Edge Function error, checking database:', error);
                    
                    const { data: registration, error: dbError } = await supabase
                        .from('registration')
                        .select('id, email_verified')
                        .eq('email_verification_token', token)
                        .maybeSingle();
                    
                    if (!dbError && registration && registration.email_verified) {
                        // Verification actually succeeded!
                        setStatus('success');
                        setMessage('Email verified successfully!');
                        setRegistrationId(registration.id);
                        hasVerifiedRef.current = true;
                        // Store verification in localStorage
                        localStorage.setItem(`email_verified_${token}`, JSON.stringify({
                            registrationId: registration.id,
                            timestamp: new Date().toISOString()
                        }));
                    } else {
                        // Show user-friendly error message instead of technical error
                        const errorMessage = error.message || '';
                        if (errorMessage.includes('non-2xx') || errorMessage.includes('404') || errorMessage.includes('406')) {
                            setStatus('error');
                            setMessage('This verification link is no longer valid. It may have already been used or expired. If you already verified your email, you can continue to your registration. Otherwise, please request a new verification email from your registration form.');
                        } else {
                            setStatus('error');
                            setMessage('We encountered an issue verifying your email. Please try again, or if the problem persists, contact support for assistance.');
                        }
                    }
                } else if (data && !data.success) {
                    setStatus('error');
                    // Show user-friendly message from server, or fallback
                    const userMessage = data.error || 'Failed to verify email. Please try again.';
                    setMessage(userMessage);
                } else {
                    // No data and no error - unexpected
                    setStatus('error');
                    setMessage('We encountered an unexpected issue. Please try again, or contact support if the problem continues.');
                }
            } catch (error) {
                console.error('Verification error:', error);
                // Show user-friendly error message
                const errorMessage = error.message || '';
                if (errorMessage.includes('non-2xx') || errorMessage.includes('404') || errorMessage.includes('406')) {
                    setStatus('error');
                    setMessage('This verification link is no longer valid. It may have already been used or expired. If you already verified your email, you can continue to your registration.');
                } else {
                    setStatus('error');
                    setMessage('We encountered an issue verifying your email. Please try again, or contact support if the problem continues.');
                }
            }
        };

        verifyEmail();
    }, [token, status]);

    const handleResendEmail = async () => {
        if (!registrationId) return;

        try {
            // Fetch registration details
            const { data: registration, error: fetchError } = await supabase
                .from('registration')
                .select('participant_email, participant_name')
                .eq('id', registrationId)
                .single();

            if (fetchError || !registration) {
                throw new Error('Registration not found');
            }

            // Send verification email
            const { data, error } = await supabase.functions.invoke('send-verification-email', {
                body: {
                    registrationId,
                    participantEmail: registration.participant_email,
                    participantName: registration.participant_name,
                    baseUrl: getAppOrigin(),
                },
            });

            if (error) throw error;

            if (data.success) {
                setMessage('Verification email has been resent. Please check your inbox.');
            } else {
                throw new Error(data.error || 'Failed to resend email');
            }
        } catch (error) {
            console.error('Resend error:', error);
            setMessage(`Failed to resend email: ${error.message}`);
        }
    };

    return (
        <Box minH="100vh" bg="gray.50" py={10}>
            <Container maxW="md">
                <Center>
                    <VStack spacing={6} align="stretch" w="100%">
                        {status === 'verifying' && (
                            <>
                                <Center>
                                    <Spinner size="xl" color="blue.500" thickness="4px" />
                                </Center>
                                <Text textAlign="center" color="gray.600">
                                    Verifying your email address...
                                </Text>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <Center>
                                    <Box
                                        bg="green.100"
                                        borderRadius="full"
                                        p={6}
                                        mb={4}
                                    >
                                        <CheckCircleIcon boxSize={12} color="green.500" />
                                    </Box>
                                </Center>
                                <Heading size="lg" textAlign="center" color="green.700">
                                    Email Verified!
                                </Heading>
                                <Alert status="success" borderRadius="md">
                                    <AlertIcon />
                                    <Text fontSize="sm">
                                        {message}
                                    </Text>
                                </Alert>
                                <Button
                                    colorScheme="blue"
                                    size="lg"
                                    onClick={() => window.close()}
                                    w="100%"
                                >
                                    Go back to registration form
                                </Button>
                            </>
                        )}

                        {status === 'expired' && (
                            <>
                                <Alert status="warning" borderRadius="md">
                                    <AlertIcon />
                                    <Text fontSize="sm">
                                        {message}
                                    </Text>
                                </Alert>
                                <Text textAlign="center" color="gray.600" fontSize="sm">
                                    Please request a new verification email to continue.
                                </Text>
                                <Button
                                    colorScheme="blue"
                                    size="lg"
                                    onClick={handleResendEmail}
                                    w="100%"
                                >
                                    Resend Verification Email
                                </Button>
                                {registrationId && (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        onClick={() => navigate(`/register/${registrationId}`)}
                                        w="100%"
                                    >
                                        Go to Registration
                                    </Button>
                                )}
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <Alert status="error" borderRadius="md">
                                    <AlertIcon />
                                    <Text fontSize="sm">
                                        {message}
                                    </Text>
                                </Alert>
                                <Text textAlign="center" color="gray.600" fontSize="sm">
                                    {message.includes('already verified') || message.includes('no longer valid')
                                        ? 'You can continue to your registration or request a new verification email if needed.'
                                        : 'If you continue to experience issues, please contact support.'}
                                </Text>
                                <VStack spacing={3} w="100%">
                                    {registrationId ? (
                                        <>
                                            <Button
                                                colorScheme="blue"
                                                size="lg"
                                                onClick={() => navigate(`/register/${registrationId}`)}
                                                w="100%"
                                            >
                                                Continue to Registration
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="md"
                                                onClick={handleResendEmail}
                                                w="100%"
                                            >
                                                Request New Verification Email
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            colorScheme="blue"
                                            size="lg"
                                            onClick={() => navigate('/register')}
                                            w="100%"
                                        >
                                            Go to Registration
                                        </Button>
                                    )}
                                </VStack>
                            </>
                        )}
                    </VStack>
                </Center>
            </Container>
        </Box>
    );
};

export default VerifyEmail;

